import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { usePageSeo } from '../hooks/usePageSeo';
import FavoriteButton from '../components/favorites/FavoriteButton';
import { useFavoritedIds } from '../hooks/useFavoritedIds';
import { useArticleSections, articlesInSection, resolveSectionName } from '../lib/articlesHierarchy';

const ArticleCard = ({ article, favorited }) => (
  <Link to={article.path} data-testid={`articles-more-card-${article.featured_article_id}`}>
    <Card className="technical-card h-full relative">
      {article.page_content_id && (
        <FavoriteButton
          contentType="page_content_article"
          contentId={article.page_content_id}
          initialFavorited={favorited}
          title={article.title}
          className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white"
        />
      )}
      <CardContent className="p-6 flex flex-col h-full">
        {article.category && (
          <Badge variant="outline" className="w-fit mb-4 font-mono text-xs uppercase tracking-wider">
            {article.category}
          </Badge>
        )}
        <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2">{article.title}</h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-1">{article.description}</p>
        <div className="flex items-center justify-between pt-4 border-t border-structure">
          <span className="text-xs text-muted-foreground">{article.reading_time}</span>
          <ChevronRight className="h-4 w-4 text-autoxium-blue" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

// Full listing of one Articles section — the counterpart of HowItWorksMoreTopicsPage.
const ArticlesMoreArticlesPage = () => {
  const { sectionSlug } = useParams();
  const navigate = useNavigate();
  const { articles, loading } = useArticleSections();

  const sectionName = resolveSectionName(articles, sectionSlug);
  const sectionArticles = useMemo(() => articlesInSection(articles, sectionSlug), [articles, sectionSlug]);
  const favoritedIds = useFavoritedIds(
    'page_content_article',
    sectionArticles.map((a) => a.page_content_id).filter(Boolean)
  );

  useEffect(() => {
    if (!loading && !sectionName) {
      navigate('/articles', { replace: true });
    }
  }, [loading, sectionName, navigate]);

  usePageSeo(
    sectionName ? `More Articles — ${sectionName} | AUTOXIUM` : undefined,
    sectionName ? `All ${sectionName} technical articles on AUTOXIUM.` : undefined
  );

  if (loading || !sectionName) {
    return (
      <div className="min-h-screen" data-testid="articles-more-loading">
        <div className="bg-chassis text-white py-16 md:py-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <Skeleton className="h-4 w-40 mb-6 bg-white/10" />
            <Skeleton className="h-10 w-1/2 bg-white/10" />
          </div>
        </div>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="articles-more-page">
      <div className="bg-chassis text-white py-16 md:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-articles"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Articles
          </Link>
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">More Articles</p>
          <h1 className="font-chivo text-3xl md:text-5xl font-black uppercase tracking-tighter">{sectionName}</h1>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sectionArticles.map((article) => (
            <ArticleCard
              key={article.featured_article_id}
              article={article}
              favorited={favoritedIds.has(article.page_content_id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArticlesMoreArticlesPage;
